import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { CLINICAL_DISCLAIMER } from '../data/ewm';
import { APPLICATIONS } from '../data/applications';
import { SCENARIOS } from '../data/scenarios';
import './Narrative.css';

/**
 * One industry page — /applications/:id
 *
 * Unknown ids fall back to a short page pointing at the hub, and the measured
 * scenarios are cross-linked so the applicability claim always reaches its
 * evidence.
 */
function ApplicationDetail() {
  const { id } = useParams();
  const index = APPLICATIONS.findIndex((a) => a.id === id);
  const application = index >= 0 ? APPLICATIONS[index] : null;

  if (!application) {
    return (
      <div className="np-page">
        <SEO
          title="Application not found | Codentic AI"
          description="That application page does not exist or has been retired."
          robots="noindex, follow"
        />
        <section className="np-hero np-hero--compact">
          <div className="container">
            <span className="np-eyebrow">Applications</span>
            <h1>That industry page is not here</h1>
            <p className="np-hero-sub">
              The link may be old or mistyped. The full list is on the Applications page.
            </p>
            <div className="np-cta-row np-cta-row--center">
              <Link to="/applications" className="np-btn np-btn--primary">All applications</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const prev = index > 0 ? APPLICATIONS[index - 1] : null;
  const next = index < APPLICATIONS.length - 1 ? APPLICATIONS[index + 1] : null;
  const related = application.scenarios
    .map((sid) => SCENARIOS.find((s) => s.id === sid))
    .filter(Boolean);

  return (
    <div className="np-page">
      <SEO
        title={`${application.name} — EWM Applications | Codentic AI`}
        description={`${application.tagline} ${application.constraint}`.slice(0, 300)}
        url={`https://www.codenticai.com/applications/${application.id}`}
      />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">
            <span aria-hidden="true">{application.icon}</span> Application
          </span>
          <h1>{application.name}</h1>
          <p className="np-hero-sub">{application.tagline}</p>
        </div>
      </section>

      {/* Why here */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">The constraint</h2>
          <p className="np-body">{application.constraint}</p>

          <h3 className="np-h3" style={{ marginTop: '26px' }}>Why memory becomes the binding problem</h3>
          <p className="np-body">{application.why}</p>
        </div>
      </section>

      {/* What it provides */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">What the side-car provides here</h2>
          <ul className="np-list">
            {application.provides.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>

          {application.notes.length > 0 && (
            <div className="np-caveat">
              <h3 className="np-h3">Stated plainly</h3>
              <ul className="np-list">
                {application.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Evidence */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">The measured work behind this</h2>
          <p className="np-lead">
            These scenario pages carry the runs. Nothing in this section is claimed without one of
            them behind it.
          </p>
          <div className="np-grid np-grid--2">
            {related.map((s) => (
              <Link key={s.id} to={`/scenarios/${s.id}`} className="np-card np-card--link">
                <div className="np-card-icon">{s.icon}</div>
                <h3 className="np-h3">{s.name}</h3>
                <p className="np-body">{s.tagline}</p>
              </Link>
            ))}
          </div>

          <div className="np-disclaimer" style={{ marginTop: '28px' }}>
            {CLINICAL_DISCLAIMER}
          </div>
        </div>
      </section>

      {/* Prev / next */}
      <section className="np-section np-section--alt">
        <div className="container np-nav-row">
          {prev ? (
            <Link to={`/applications/${prev.id}`} className="np-nav-link">
              <span className="np-nav-dir">Previous industry</span>
              <span className="np-nav-name">
                <span aria-hidden="true">{prev.icon}</span> {prev.name}
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/applications/${next.id}`} className="np-nav-link np-nav-link--right">
              <span className="np-nav-dir">Next industry</span>
              <span className="np-nav-name">
                <span aria-hidden="true">{next.icon}</span> {next.name}
              </span>
            </Link>
          ) : (
            <span />
          )}
        </div>
        <div className="container">
          <div className="np-cta-row np-cta-row--center">
            <Link to="/applications" className="np-btn np-btn--secondary">All applications</Link>
            <Link to="/partners" className="np-btn np-btn--primary">Become a design partner</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ApplicationDetail;
